import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Chip, IconButton, Typography,
  Switch, FormControlLabel, CircularProgress, Divider, Alert,
  MenuItem, Tooltip,
} from '@mui/material';
import { Add, Close, Delete, CloudUpload, Info } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import { stopWheelNudge } from '../utils/numberInput';
import BranchSelect from './BranchSelect';
import { useMyLocations } from '../hooks/useMyLocations';

interface VariantRow {
  label: string;
  sku: string;
  priceOverride: string;
  stockQuantity: string;
  isActive: boolean;
  /** True once saved — its stock is server-owned and no longer editable here. */
  existing?: boolean;
}

interface MerchandiseFormData {
  title: string;
  description: string;
  images: string[];
  price: string;
  category: string;
  tags: string[];
  isActive: boolean;
  sku: string;
  stockQuantity: string;
  variants: VariantRow[];
  checkoutEnabled: boolean;
  payAtStudio: boolean;
  gstRate: string;
  hsnCode: string;
  studioId: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** `studioId`: the location a studio-role write is pinned to (see handleSubmit). */
  onSave: (data: any, studioId?: string) => void;
  initialData?: any;
  studios?: { _id: string; studioName: string }[];
  loading?: boolean;
  /** Pre-selects the studio when the page is already scoped to one. */
  defaultStudioId?: string;
}

const DEFAULT_FORM: MerchandiseFormData = {
  title: '',
  description: '',
  images: [],
  price: '',
  category: '',
  tags: [],
  isActive: true,
  sku: '',
  stockQuantity: '',
  variants: [],
  checkoutEnabled: false,
  payAtStudio: false,
  gstRate: '18',
  hsnCode: '',
  studioId: '',
};

export default function MerchandiseFormDialog({
  open, onClose, onSave, initialData, studios = [], loading, defaultStudioId,
}: Props) {
  const { admin } = useAuth();
  const isStudio = admin?.role !== 'super_admin' && admin?.role !== 'admin';
  // Under "All locations" a new item has no implied home — the switcher sends
  // __all__, which the server refuses for writes — so a studio role picks one.
  const { isAllLocations } = useMyLocations();
  const mustPickBranch = isStudio && isAllLocations && !initialData;
  const [form, setForm] = useState<MerchandiseFormData>(DEFAULT_FORM);
  const [tagInput, setTagInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    if (initialData) {
      setForm({
        title: initialData.title || '',
        description: initialData.description || '',
        images: initialData.images?.length ? initialData.images : [],
        price: String(initialData.price ?? ''),
        category: initialData.category || '',
        tags: initialData.tags || [],
        isActive: initialData.isActive ?? true,
        sku: initialData.sku || '',
        stockQuantity: String(initialData.stockQuantity ?? ''),
        variants: (initialData.variants || []).map((v: any) => ({
          label: v.label ?? '',
          sku: v.sku ?? '',
          priceOverride: v.priceOverride == null ? '' : String(v.priceOverride),
          stockQuantity: String(v.stockQuantity ?? 0),
          isActive: v.isActive !== false,
          existing: true,
        })),
        checkoutEnabled: initialData.checkoutEnabled ?? false,
        payAtStudio: initialData.payAtStudio ?? false,
        gstRate: String(initialData.gstRate ?? 18),
        hsnCode: initialData.hsnCode || '',
        studioId: initialData.studio?._id || initialData.studio || '',
      });
    } else {
      // Under "All locations" the location is a question for the operator
      // (BranchSelect starts empty) — never a pre-filled default.
      setForm({ ...DEFAULT_FORM, studioId: (mustPickBranch ? undefined : defaultStudioId) ?? '' });
      setTagInput('');
    }
    setUploadError(null);
  }, [open, initialData, defaultStudioId, mustPickBranch]);

  const set = (field: keyof MerchandiseFormData, value: any) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const hasVariants = form.variants.length > 0;

  // ── Images ────────────────────────────────────────────────────────────────

  /**
   * Real upload, replacing the hand-typed image URLs this form used to take.
   * `uploadFiles` has existed on apiService all along — it was simply never
   * wired in here.
   */
  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;

    const picked = Array.from(files);
    for (const file of picked) {
      if (!file.type.startsWith('image/')) {
        setUploadError(`"${file.name}" is not an image`);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setUploadError(`"${file.name}" must be under 5 MB`);
        return;
      }
    }

    setUploading(true);
    setUploadError(null);
    try {
      const res: any = await apiService.uploadFiles(picked, 'merchandise');
      const urls: string[] = res?.fileLinks || res?.data?.fileLinks || [];
      if (!urls.length) throw new Error('Upload returned no URL');
      set('images', [...form.images, ...urls]);
    } catch (err: any) {
      setUploadError(err?.message || err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const removeImage = (idx: number) => set('images', form.images.filter((_, i) => i !== idx));

  // ── Tags ──────────────────────────────────────────────────────────────────

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) set('tags', [...form.tags, t]);
    setTagInput('');
  };
  const removeTag = (t: string) => set('tags', form.tags.filter(x => x !== t));

  // ── Variants ──────────────────────────────────────────────────────────────

  const addVariant = () =>
    set('variants', [
      ...form.variants,
      { label: '', sku: '', priceOverride: '', stockQuantity: '0', isActive: true },
    ]);

  const setVariant = (idx: number, field: keyof VariantRow, value: any) =>
    set('variants', form.variants.map((v, i) => (i === idx ? { ...v, [field]: value } : v)));

  const removeVariant = (idx: number) =>
    set('variants', form.variants.filter((_, i) => i !== idx));

  // ── Validation ────────────────────────────────────────────────────────────

  const variantSkus = form.variants.map(v => v.sku.trim()).filter(Boolean);
  const dupSku = new Set(variantSkus).size !== variantSkus.length;
  const variantsIncomplete = form.variants.some(v => !v.label.trim() || !v.sku.trim());
  const needsStudio = (!isStudio || mustPickBranch) && !initialData && !form.studioId;

  const error =
    needsStudio ? 'Choose a studio'
    : !form.title.trim() ? 'Title is required'
    : form.images.length === 0 ? 'Add at least one image'
    : variantsIncomplete ? 'Every variant needs a label and a SKU'
    : dupSku ? 'Variant SKUs must be unique'
    : null;

  const handleSubmit = () => {
    if (error) return;
    const payload: any = {
      title: form.title.trim(),
      description: form.description || undefined,
      images: form.images,
      price: form.price ? Number(form.price) : 0,
      category: form.category || undefined,
      tags: form.tags.length ? form.tags : undefined,
      isActive: form.isActive,
      checkoutEnabled: form.checkoutEnabled,
      payAtStudio: form.payAtStudio,
      gstRate: form.gstRate ? Number(form.gstRate) : 18,
      hsnCode: form.hsnCode || undefined,
    };

    if (hasVariants) {
      payload.variants = form.variants.map(v => ({
        label: v.label.trim(),
        sku: v.sku.trim(),
        priceOverride: v.priceOverride === '' ? null : Number(v.priceOverride),
        // Only NEW variants carry opening stock. For existing ones the server
        // ignores this and keeps its own count — stock moves through the stock
        // endpoint, never through a product edit.
        stockQuantity: v.existing ? undefined : Number(v.stockQuantity || 0),
        isActive: v.isActive,
      }));
    } else {
      payload.sku = form.sku || undefined;
      // Same rule for simple products: opening stock only, then hands off.
      if (!initialData) {
        payload.stockQuantity = form.stockQuantity ? Number(form.stockQuantity) : 0;
      }
    }

    if (initialData) payload.merchandiseId = initialData._id;
    if (!isStudio && form.studioId) payload.studioId = form.studioId;
    // A studio role's write is pinned to the item's location (edit) or the one
    // chosen above (create under "All"); empty keeps today's behaviour.
    onSave(payload, isStudio ? form.studioId || undefined : undefined);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {initialData ? 'Edit Item' : 'Add Merchandise'}
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2.5} pt={1}>

          {!isStudio && !initialData && (
            <TextField
              select label="Studio" value={form.studioId} required size="small"
              onChange={e => set('studioId', e.target.value)}
            >
              <MenuItem value="">Select studio</MenuItem>
              {studios.map(s => <MenuItem key={s._id} value={s._id}>{s.studioName}</MenuItem>)}
            </TextField>
          )}

          {mustPickBranch && (
            <BranchSelect value={form.studioId} onChange={(v) => set('studioId', v)} minRole="owner" />
          )}

          <TextField label="Title" value={form.title} required size="small"
            onChange={e => set('title', e.target.value)} />

          <TextField label="Description" value={form.description} multiline rows={3} size="small"
            onChange={e => set('description', e.target.value)} />

          <Box display="flex" gap={2}>
            <TextField
              label={hasVariants ? 'Base price (₹)' : 'Price (₹)'} value={form.price}
              size="small" type="number" inputProps={{ min: 0 }} sx={{ flex: 1 }}
              helperText={hasVariants ? 'Variants inherit this unless they override it' : ' '}
              onWheel={stopWheelNudge}
              onChange={e => set('price', e.target.value)}
            />
            <TextField label="Category" value={form.category} size="small" sx={{ flex: 1 }}
              helperText="Drives the app's category rail"
              onChange={e => set('category', e.target.value)} />
          </Box>

          {/* ── Images ── */}
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
              IMAGES
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
              {form.images.map((img, idx) => (
                <Box key={idx} sx={{ position: 'relative', width: 88, height: 88 }}>
                  <Box component="img" src={img} alt="" sx={{
                    width: '100%', height: '100%', objectFit: 'cover',
                    borderRadius: 1, border: '1px solid', borderColor: 'divider',
                  }} />
                  <IconButton size="small" onClick={() => removeImage(idx)}
                    sx={{
                      position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper',
                      border: '1px solid', borderColor: 'divider',
                      '&:hover': { bgcolor: 'error.light' },
                    }}>
                    <Delete sx={{ fontSize: 14 }} />
                  </IconButton>
                  {idx === 0 && (
                    <Chip label="Cover" size="small" sx={{ position: 'absolute', bottom: 4, left: 4, height: 18, fontSize: 10 }} />
                  )}
                </Box>
              ))}
            </Box>
            <input ref={fileInput} type="file" accept="image/*" multiple hidden
              onChange={e => handleFiles(e.target.files)} />
            <Button size="small" startIcon={uploading ? <CircularProgress size={14} /> : <CloudUpload />}
              onClick={() => fileInput.current?.click()} disabled={uploading}>
              {uploading ? 'Uploading…' : 'Upload images'}
            </Button>
            {uploadError && <Alert severity="error" sx={{ mt: 1 }}>{uploadError}</Alert>}
          </Box>

          {/* ── Tags ── */}
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
              TAGS (optional)
            </Typography>
            <Box display="flex" gap={1} mb={1}>
              <TextField size="small" placeholder="Add tag" value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} />
              <Button variant="outlined" size="small" onClick={addTag}>Add</Button>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {form.tags.map(t => <Chip key={t} label={t} size="small" onDelete={() => removeTag(t)} />)}
            </Box>
          </Box>

          <Divider />

          {/* ── Inventory ── */}
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                INVENTORY
              </Typography>
              <Tooltip title="Add variants when an item comes in sizes, colours or flavours — stock is then tracked per variant, because '12 hoodies' is meaningless if it's 2×S and 10×XL.">
                <Info sx={{ fontSize: 14, color: 'text.disabled' }} />
              </Tooltip>
            </Box>

            {!hasVariants && (
              <Box display="flex" gap={2} mb={1.5}>
                <TextField label="SKU" value={form.sku} size="small" sx={{ flex: 1 }}
                  onChange={e => set('sku', e.target.value)} />
                <TextField
                  label={initialData ? 'Stock (managed separately)' : 'Opening stock'}
                  value={initialData ? (initialData.stockQuantity ?? 0) : form.stockQuantity}
                  size="small" type="number" inputProps={{ min: 0 }} sx={{ flex: 1 }}
                  disabled={!!initialData}
                  helperText={initialData ? 'Use Adjust stock to change' : ' '}
                  onWheel={stopWheelNudge}
                  onChange={e => set('stockQuantity', e.target.value)}
                />
              </Box>
            )}

            {form.variants.map((v, idx) => (
              <Box key={idx} display="flex" gap={1} mb={1} alignItems="flex-start">
                <TextField label="Option" placeholder="S / Vanilla" value={v.label} size="small" sx={{ flex: 1 }}
                  onChange={e => setVariant(idx, 'label', e.target.value)} />
                <TextField label="SKU" value={v.sku} size="small" sx={{ flex: 1.2 }}
                  disabled={v.existing}
                  helperText={v.existing ? 'Fixed once ordered' : ' '}
                  onChange={e => setVariant(idx, 'sku', e.target.value)} />
                <TextField label="Price ₹" placeholder="inherit" value={v.priceOverride}
                  size="small" type="number" inputProps={{ min: 0 }} sx={{ width: 110 }}
                  onWheel={stopWheelNudge}
                  onChange={e => setVariant(idx, 'priceOverride', e.target.value)} />
                <TextField
                  label={v.existing ? 'Stock' : 'Opening'}
                  value={v.stockQuantity} size="small" type="number"
                  inputProps={{ min: 0 }} sx={{ width: 100 }}
                  disabled={v.existing}
                  onWheel={stopWheelNudge}
                  onChange={e => setVariant(idx, 'stockQuantity', e.target.value)}
                />
                <Tooltip title={v.isActive ? 'Active' : 'Hidden'}>
                  <Switch size="small" checked={v.isActive} sx={{ mt: 0.5 }}
                    onChange={e => setVariant(idx, 'isActive', e.target.checked)} />
                </Tooltip>
                <IconButton size="small" color="error" sx={{ mt: 0.5 }} onClick={() => removeVariant(idx)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}

            <Button size="small" startIcon={<Add />} onClick={addVariant}>Add variant</Button>

            {hasVariants && initialData && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                Stock for saved variants changes only via <strong>Adjust stock</strong>, so a
                product edit can never overwrite what a live checkout just reserved.
                A variant that has been ordered can be deactivated but not removed.
              </Alert>
            )}
          </Box>

          <Divider />

          {/* ── Selling ── */}
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
              SELLING
            </Typography>
            <FormControlLabel
              control={<Switch checked={form.checkoutEnabled} color="primary"
                onChange={e => set('checkoutEnabled', e.target.checked)} />}
              label="Sell in the app (checkout)"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5, mb: 1 }}>
              Off = browse-only; the app shows “Enquire at Studio” instead of Add to Bag.
            </Typography>
            <FormControlLabel
              control={<Switch checked={form.payAtStudio} color="primary"
                disabled={!form.checkoutEnabled}
                onChange={e => set('payAtStudio', e.target.checked)} />}
              label="Allow pay at studio"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5 }}>
              Member reserves in the app and settles at the counter on collection.
            </Typography>

            <Box display="flex" gap={2} mt={2}>
              <TextField label="GST %" value={form.gstRate} size="small" type="number"
                inputProps={{ min: 0, max: 100 }} sx={{ width: 120 }}
                helperText="18% goods"
                onWheel={stopWheelNudge}
                onChange={e => set('gstRate', e.target.value)} />
              <TextField label="HSN code" value={form.hsnCode} size="small" sx={{ flex: 1 }}
                helperText="For the tax invoice"
                onChange={e => set('hsnCode', e.target.value)} />
            </Box>
          </Box>

          <FormControlLabel
            control={<Switch checked={form.isActive} onChange={e => set('isActive', e.target.checked)} color="primary" />}
            label="Active (visible in the app)"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {/* Say WHY the button is disabled — it used to just sit there dead. */}
        {error && <Typography variant="caption" color="error" sx={{ mr: 'auto' }}>{error}</Typography>}
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading || !!error || uploading}>
          {loading ? <CircularProgress size={18} /> : initialData ? 'Save Changes' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
